import { createFileRoute } from "@tanstack/react-router";
import { useState, type InputHTMLAttributes } from "react";
import { Check, Gift, Lock, ShieldCheck, BadgeCheck } from "lucide-react";
import { CERT_DATA } from "@/lib/certificacao/data";

const HERO_BG = CERT_DATA.quadril.heroBg;
const QUADRIL_LOGO = CERT_DATA.quadril.logo;

/** Bônus e checkouts Eduzz (cupom GRUPOVIP) — idênticos à página de referência. */
const BONUS = [
  { id: "coluna-lombar", name: "Certificação em Coluna Lombar", url: "https://chk.eduzz.com/Q9N225BK01?cupom=GRUPOVIP&df=1&pf=1" },
  { id: "coluna-cervical", name: "Certificação em Coluna Cervical", url: "https://chk.eduzz.com/D0R88A719Y?cupom=GRUPOVIP&df=1&pf=1" },
  { id: "ombro", name: "Certificação em Ombro", url: "https://chk.eduzz.com/R9JXXJ8Y0X?cupom=GRUPOVIP&df=1&pf=1" },
  { id: "tornozelo-pe", name: "Certificação em Tornozelo e Pé", url: "https://chk.eduzz.com/8WPNNA4Q0P?cupom=GRUPOVIP&df=1&pf=1" },
  { id: "joelho", name: "Certificação em Joelho", url: "https://chk.eduzz.com/R9JXXJ6P0X?cupom=GRUPOVIP&df=1&pf=1" },
  { id: "agentes-eletrofisicos", name: "Certificação em Agentes Eletrofísicos", url: "https://chk.eduzz.com/G92KKERXWE?cupom=GRUPOVIP&df=1&pf=1" },
] as const;
const DEFAULT_URL = BONUS[0].url;

const onlyDigits = (v: string) => v.replace(/\D/g, "");

function maskDoc(v: string) {
  const d = onlyDigits(v).slice(0, 14);
  return d.length <= 11
    ? d.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    : d.replace(/(\d{2})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1/$2").replace(/(\d{4})(\d{1,2})$/, "$1-$2");
}
function maskPhone(v: string) {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}
function isCpf(v: string) {
  const s = onlyDigits(v);
  if (s.length !== 11 || /^(\d)\1+$/.test(s)) return false;
  const n = s.split("").map(Number);
  for (const len of [9, 10]) {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += n[i] * (len + 1 - i);
    if (((sum * 10) % 11) % 10 !== n[len]) return false;
  }
  return true;
}
function isCnpj(v: string) {
  const s = onlyDigits(v);
  if (s.length !== 14 || /^(\d)\1+$/.test(s)) return false;
  const n = s.split("").map(Number);
  const dv = (len: 12 | 13) => {
    const w = len === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const r = w.reduce((a, x, i) => a + x * n[i], 0) % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return dv(12) === n[12] && dv(13) === n[13];
}
const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(v.trim()) && v.length <= 255;
function isCel(v: string) {
  const d = onlyDigits(v);
  if (d.length !== 11) return false;
  const ddd = Number(d.slice(0, 2));
  return ddd >= 11 && ddd <= 99 && d.charAt(2) === "9";
}

function buildCheckoutUrl(base: string, c: { name: string; email: string; doc: string; phone: string }) {
  const u = new URL(base);
  u.searchParams.set("name", c.name);
  u.searchParams.set("email", c.email);
  u.searchParams.set("doc", c.doc);
  u.searchParams.set("cel", c.phone);
  return u.toString();
}

export const Route = createFileRoute("/certificacao-quadril-oferta")({
  head: () => ({
    meta: [
      { title: "Oferta de lançamento · Certificação em Quadril" },
      { name: "description", content: "Oferta exclusiva do grupo: Certificação em Quadril por R$197 com uma certificação bônus gratuita." },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Oferta de lançamento · Certificação em Quadril" },
      { property: "og:description", content: "Certificação em Quadril por R$197 com uma certificação bônus gratuita à sua escolha." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://fisioemortopedia.com.br/certificacao-quadril-oferta" },
      ...(HERO_BG ? [{ rel: "preload", as: "image", href: HERO_BG, fetchPriority: "high" as const }] : []),
    ],
  }),
  component: OfertaQuadril,
});

type Form = { name: string; email: string; email2: string; doc: string; phone: string };
type Errors = Partial<Record<keyof Form | "bonus", string>>;

function Field({ label, error, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[13px] font-medium text-[var(--qo-muted)]">{label}</span>
      <input
        {...props}
        className={`h-11 w-full rounded-lg border bg-[var(--qo-input)] px-3.5 text-[15px] text-[var(--qo-fg)] outline-none transition placeholder:text-[var(--qo-muted)]/60 focus:border-[var(--qo-pink)] focus:ring-2 focus:ring-[var(--qo-pink)]/25 ${
          error ? "border-destructive" : "border-[var(--qo-border)]"
        }`}
      />
      {error && <span className="mt-1 block text-xs text-destructive">{error}</span>}
    </label>
  );
}

function OfertaQuadril() {
  const [f, setF] = useState<Form>({ name: "", email: "", email2: "", doc: "", phone: "" });
  const [bonus, setBonus] = useState<string | null>(null);
  const [err, setErr] = useState<Errors>({});
  const [sending, setSending] = useState(false);

  const set = (k: keyof Form, v: string) => {
    setF((p) => ({ ...p, [k]: v }));
    if (err[k]) setErr((p) => ({ ...p, [k]: "" }));
  };

  function validate(): Errors {
    const e: Errors = {};
    const name = f.name.trim();
    if (!name) e.name = "Informe seu nome completo.";
    else if (name.split(/\s+/).length < 2) e.name = "Digite nome e sobrenome.";
    const doc = onlyDigits(f.doc);
    if (!doc) e.doc = "Informe seu CPF ou CNPJ.";
    else if (doc.length === 11 ? !isCpf(doc) : doc.length !== 14 || !isCnpj(doc))
      e.doc = doc.length <= 11 ? "CPF inválido. Verifique os números." : "CNPJ inválido. Verifique os números.";
    if (!f.email.trim()) e.email = "Informe seu e-mail.";
    else if (!isEmail(f.email)) e.email = "E-mail inválido. Ex: nome@email.com";
    if (!f.email2.trim()) e.email2 = "Confirme seu e-mail.";
    else if (f.email2.trim().toLowerCase() !== f.email.trim().toLowerCase()) e.email2 = "Os e-mails não conferem.";
    if (!f.phone) e.phone = "Informe seu celular.";
    else if (!isCel(f.phone)) e.phone = "Celular inválido. Use DDD + 9 dígitos.";
    if (!bonus) e.bonus = "Selecione uma certificação bônus para continuar.";
    return e;
  }

  function submit(ev: React.FormEvent) {
    ev.preventDefault();
    const e = validate();
    setErr(e);
    if (Object.values(e).some(Boolean)) {
      document.querySelector("[data-first-error]")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const customer = {
      name: f.name.trim(),
      email: f.email.trim().toLowerCase(),
      doc: onlyDigits(f.doc),
      phone: onlyDigits(f.phone),
    };
    const base = BONUS.find((b) => b.id === bonus)?.url ?? DEFAULT_URL;
    setSending(true);
    window.location.href = buildCheckoutUrl(base, customer);
  }

  let marked = false;
  const first = (k: keyof Errors) => (err[k] && !marked ? ((marked = true), { "data-first-error": true }) : {});

  return (
    <div className="quadril-oferta min-h-screen bg-[var(--qo-bg)] text-[var(--qo-fg)]">
      <header className="relative overflow-hidden">
        {HERO_BG && (
          <img src={HERO_BG} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" decoding="async" fetchPriority="high" width={1920} height={640} />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--qo-bg)]/40 to-[var(--qo-bg)]" />
        <div className="relative mx-auto flex max-w-[680px] flex-col items-center px-5 pb-16 pt-16 text-center">
          {QUADRIL_LOGO && (
            <img src={QUADRIL_LOGO} alt="Certificação Quadril" className="h-[58px] w-auto sm:h-[72px]" decoding="async" />
          )}
          <h1 className="mt-9 text-[18px] font-extrabold leading-tight tracking-tight text-[var(--qo-title)] [filter:var(--qo-title-glow)] sm:text-[22px]">OFERTA DE LANÇAMENTO</h1>
          <div className="mx-auto mt-5 w-full max-w-[420px] rounded-2xl border border-[var(--qo-pink)]/45 bg-[var(--qo-pink)]/12 px-5 py-4 shadow-[0_22px_60px_-30px_var(--qo-shadow)]">
            <p className="text-[13px] text-[var(--qo-muted)]">DE <span className="line-through decoration-[1.5px]">R$697</span> POR</p>
            <p className="mt-1 flex flex-wrap items-baseline justify-center gap-x-2">
              <strong className="text-[44px] font-black leading-none tracking-tight text-[var(--qo-pink)]">R$ 197</strong>
            </p>
            <p className="mt-2 text-[15px] text-[var(--qo-muted)]">
              à vista ou até <strong className="text-lg font-extrabold text-[var(--qo-fg)]">12x R$ 20,37</strong>
            </p>
          </div>
          <p className="mt-7 text-[15px] text-[var(--qo-fg)]">Preencha seus dados e escolha sua <span className="font-bold">certificação bônus</span> gratuita.</p>
        </div>
      </header>

      {/* Do fim do cabeçalho para baixo: tema claro, como na referência */}
      <div className="quadril-oferta-body bg-[var(--qo-bg)] text-[var(--qo-fg)]">
      <main className="mx-auto w-full max-w-[640px] px-4 pb-20 pt-16">
        <form onSubmit={submit} noValidate className="space-y-6">
          <section className="rounded-2xl border border-[var(--qo-border)] bg-[var(--qo-card)] p-5 shadow-[0_20px_60px_-30px_var(--qo-shadow)] sm:p-6">
            <div className="grid gap-4">
              <div {...first("name")}><Field label="Nome Completo ou Razão Social" autoComplete="name" value={f.name} onChange={(e) => set("name", e.target.value)} error={err.name} /></div>
              <div {...first("doc")}><Field label="CPF ou CNPJ" inputMode="numeric" value={f.doc} onChange={(e) => set("doc", maskDoc(e.target.value))} error={err.doc} /></div>
              <div {...first("email")}><Field label="E-mail" type="email" autoComplete="email" value={f.email} onChange={(e) => set("email", e.target.value)} error={err.email} /></div>
              <div {...first("email2")}><Field label="Confirme seu E-mail" type="email" autoComplete="email" value={f.email2} onChange={(e) => set("email2", e.target.value)} error={err.email2} /></div>
              <div className="grid grid-cols-[96px_1fr] gap-3" {...first("phone")}>
                <label className="block">
                  <span className="mb-1 block text-[13px] font-medium text-[var(--qo-muted)]">DDI</span>
                  <div className="flex h-11 items-center gap-1.5 rounded-lg border border-[var(--qo-border)] bg-[var(--qo-input)] px-3 text-[15px] font-semibold"><span className="text-[var(--qo-muted)]">BR</span> +55</div>
                </label>
                <Field label="Celular" type="tel" inputMode="numeric" autoComplete="tel-national" value={f.phone} onChange={(e) => set("phone", maskPhone(e.target.value))} error={err.phone} />
              </div>
            </div>
          </section>

          <section {...first("bonus")} className="rounded-2xl border border-[var(--qo-border)] bg-[var(--qo-card)] p-5 sm:p-6">
            <h2 className="flex items-center gap-2 text-sm font-extrabold tracking-[0.14em] text-[var(--qo-pink)]"><Gift className="h-4 w-4" /> ESCOLHA SEU BÔNUS GRATUITO</h2>
            <p className="mt-1 text-sm text-[var(--qo-muted)]">Você pode escolher <strong className="text-[var(--qo-fg)]">apenas 1</strong> certificação bônus.</p>
            <div role="radiogroup" className="mt-4 grid gap-2.5">
              {BONUS.map((b) => {
                const on = bonus === b.id;
                return (
                  <button
                    type="button"
                    role="radio"
                    aria-checked={on}
                    key={b.id}
                    onClick={() => { setBonus(b.id); setErr((p) => ({ ...p, bonus: "" })); }}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition ${
                      on ? "border-[var(--qo-pink)] bg-[var(--qo-pink)]/10 ring-2 ring-[var(--qo-pink)]/30" : "border-[var(--qo-border)] bg-[var(--qo-input)] hover:border-[var(--qo-purple)]"
                    }`}
                  >
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${on ? "border-[var(--qo-pink)] bg-[var(--qo-pink)]" : "border-[var(--qo-muted)]"}`}>
                      {on && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                    </span>
                    <span className="flex-1 text-[15px] font-semibold">{b.name}</span>
                    <span className="rounded-full bg-[var(--qo-green)]/12 px-2.5 py-0.5 text-[11px] font-bold uppercase text-[var(--qo-green)]">Grátis</span>
                  </button>
                );
              })}
            </div>
            {err.bonus && <p className="mt-3 text-sm text-destructive">{err.bonus}</p>}
          </section>

          <button
            type="submit"
            disabled={sending}
            className="w-full rounded-xl bg-gradient-to-r from-[var(--qo-pink)] to-[var(--qo-purple)] px-5 py-4 text-[15px] font-extrabold uppercase tracking-wide text-white shadow-[0_12px_40px_-12px_var(--qo-pink)] transition hover:brightness-110 disabled:opacity-70"
          >
            {sending ? "Redirecionando para o pagamento..." : "Garantir meu bônus e ir para o pagamento seguro"}
          </button>

          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-[var(--qo-muted)]">
            <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Compra segura</span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Dados protegidos</span>
            <span className="flex items-center gap-1.5"><BadgeCheck className="h-3.5 w-3.5" /> Garantia de 7 dias</span>
          </div>
        </form>
      </main>
      <footer className="border-t border-[var(--qo-border)]/50 bg-[var(--qo-footer)] px-5 py-6">
        <p className="mx-auto max-w-[640px] text-center text-xs text-[var(--qo-muted)]">Fisio em Ortopedia · Pagamento processado pela Eduzz</p>
      </footer>
      </div>
    </div>
  );
}
